import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, errMsg } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@finance/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@finance/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { toast } from "sonner";
import { LoanScheduleEditor } from "@finance/components/LoanScheduleEditor";
import { Plus, CheckCircle, XCircle, HandCoins, Trash } from "@phosphor-icons/react";

const STATUS_STYLES = {
  Pending: "bg-amber-50 text-amber-700", Approved: "bg-blue-50 text-blue-700",
  Active: "bg-indigo-50 text-indigo-700", "Fully Repaid": "bg-emerald-50 text-emerald-700",
  Rejected: "bg-red-50 text-red-600", Cancelled: "bg-gray-100 text-gray-500",
};

function Badge({ status }) {
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
}

function evenSplit(principal, n) {
  principal = Math.round(Number(principal || 0) * 100) / 100;
  if (!n || n < 1) return [];
  const inst = Math.round((principal / n) * 100) / 100;
  const arr = [];
  let alloc = 0;
  for (let i = 0; i < n; i++) {
    const amt = i === n - 1 ? Math.round((principal - alloc) * 100) / 100 : inst;
    arr.push(amt);
    alloc = Math.round((alloc + amt) * 100) / 100;
  }
  return arr;
}

function addMonths(period, n) {
  if (!period || period.length < 7) return "";
  const y = parseInt(period.slice(0, 4), 10), m = parseInt(period.slice(5, 7), 10);
  const idx = y * 12 + (m - 1) + n;
  return `${String(Math.floor(idx / 12)).padStart(4, "0")}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

export function AdvancesLoans({ employees = [] }) {
  const { user } = useAuth();
  const isExec = !!user?.is_executive;
  const [rows, setRows] = useState([]);
  const [banks, setBanks] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  const [form, setForm] = useState({ type: "Advance" });
  const [remit, setRemit] = useState(null);   // record being disbursed
  const [rf, setRf] = useState({});
  const [detail, setDetail] = useState(null);
  const [skipMonth, setSkipMonth] = useState("");

  const refreshDetail = async (id) => { try { const { data } = await api.get(`/payroll/advances/${id}`); setDetail(data); } catch { /* ignore */ } };

  const doSkip = async () => {
    if (!skipMonth) return toast.error("Pick a month to skip");
    try { await api.post(`/payroll/advances/${detail.id}/skip-month`, { period: skipMonth }); toast.success(`Paused ${skipMonth} — schedule extended`); setSkipMonth(""); await refreshDetail(detail.id); load(); }
    catch (e) { toast.error(errMsg(e) || "Could not skip"); }
  };
  const doUnskip = async (period) => {
    try { await api.post(`/payroll/advances/${detail.id}/unskip-month`, { period }); toast.success(`Resumed ${period}`); await refreshDetail(detail.id); load(); }
    catch (e) { toast.error(errMsg(e) || "Could not resume"); }
  };

  const load = useCallback(() => api.get("/payroll/advances").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => {}); }, [load]);

  const book = async () => {
    if (!form.employee_id) return toast.error("Select an employee");
    if (!(Number(form.principal) > 0)) return toast.error("Enter a principal amount");
    try {
      await api.post("/payroll/advances", {
        employee_id: form.employee_id, type: form.type, principal: Number(form.principal),
        date_requested: form.date_requested || null, reason: form.reason || null,
      });
      toast.success("Advance/Loan booked (Pending approval)");
      setBookOpen(false); setForm({ type: "Advance" }); load();
    } catch (e) { toast.error(errMsg(e) || "Could not book"); }
  };

  const act = async (id, path, label) => {
    try { await api.post(`/payroll/advances/${id}/${path}`); toast.success(label); load(); if (detail?.id === id) { const { data } = await api.get(`/payroll/advances/${id}`); setDetail(data); } }
    catch (e) { toast.error(errMsg(e) || "Action failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/payroll/advances/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e) || "Cannot delete"); }
  };

  const openRemit = (r) => {
    setRf({ schedule_type: "one_off", disburse_date: new Date().toISOString().slice(0, 10), start_period: new Date().toISOString().slice(0, 7), months: 3, custom: [] });
    setRemit(r);
  };

  const confirmRemit = async () => {
    if (!rf.bank_account_id) return toast.error("Select the disbursement bank");
    if (!rf.start_period) return toast.error("Select the repayment start month");
    const custom = rf.schedule_type === "amortized" ? (rf.custom || []).map((x) => Number(x || 0)) : null;
    if (custom) {
      const tot = Math.round(custom.reduce((a, x) => a + x, 0) * 100) / 100;
      if (Math.abs(tot - Number(remit.principal || 0)) > 0.01) {
        return toast.error(`Monthly amounts must total the principal (${fmtNum(remit.principal)}); currently ${fmtNum(tot)}.`);
      }
    }
    try {
      await api.post(`/payroll/advances/${remit.id}/confirm-remittance`, {
        disburse_date: rf.disburse_date, bank_account_id: rf.bank_account_id, reference_no: rf.reference_no || null,
        schedule_type: rf.schedule_type, months: custom ? custom.length : null,
        custom_schedule: custom, start_period: rf.start_period,
      });
      toast.success("Remittance confirmed — disbursed & posted to GL");
      setRemit(null); load();
    } catch (e) { toast.error(errMsg(e) || "Could not confirm"); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="book-advance-btn" onClick={() => { setForm({ type: "Advance", date_requested: new Date().toISOString().slice(0, 10) }); setBookOpen(true); }}>
          <Plus size={16} className="mr-1" />Book Advance / Loan
        </Button>
      </div>
      <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
            <th className="px-4 py-2 text-left">Employee</th><th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-right">Principal</th><th className="px-4 py-2 text-right">Outstanding</th>
            <th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No advances or loans yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`advance-row-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => setDetail(r)}>{r.employee_name}</td>
                <td className="px-4 py-2">{r.type}</td>
                <td className="px-4 py-2 text-right lx-num">{fmtNum(r.principal)}</td>
                <td className="px-4 py-2 text-right lx-num font-semibold">{fmtNum(r.outstanding)}</td>
                <td className="px-4 py-2"><Badge status={r.status} /></td>
                <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                  {r.status === "Pending" && isExec && <>
                    <button className="text-xs text-emerald-600 hover:underline" data-testid={`approve-advance-${r.id}`} onClick={() => act(r.id, "approve", "Approved")}>Approve</button>
                    <button className="text-xs text-red-500 hover:underline" data-testid={`reject-advance-${r.id}`} onClick={() => act(r.id, "reject", "Rejected")}>Reject</button>
                  </>}
                  {r.status === "Pending" && !isExec && <span className="text-xs italic text-gray-400" data-testid={`awaiting-nishat-${r.id}`}>Awaiting Ronick</span>}
                  {r.status === "Approved" && <button className="text-xs text-blue-600 hover:underline" data-testid={`remit-advance-${r.id}`} onClick={() => openRemit(r)}>Confirm Remittance</button>}
                  {(r.status === "Active" || r.status === "Fully Repaid") && <button className="text-xs text-gray-600 hover:underline" data-testid={`view-advance-${r.id}`} onClick={() => setDetail(r)}>View</button>}
                  {r.status === "Active" && (r.repaid || 0) === 0 && <button className="text-xs text-red-500 hover:underline" data-testid={`cancel-advance-${r.id}`} onClick={() => act(r.id, "cancel", "Cancelled")}>Cancel</button>}
                  {["Pending", "Rejected", "Cancelled"].includes(r.status) && <button className="text-xs text-gray-400 hover:text-red-500" data-testid={`delete-advance-${r.id}`} onClick={() => del(r.id)}><Trash size={13} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Booking dialog */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="max-w-lg" data-testid="book-advance-dialog">
          <DialogHeader><DialogTitle className="font-display">Book Advance / Loan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="lx-label">Employee</Label>
              <SearchableSelect className="mt-1" testid="advance-employee" placeholder="Select employee"
                value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })}
                options={employees.map((e) => ({ value: e.id, label: e.name }))} />
            </div>
            <div><Label className="lx-label">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1" data-testid="advance-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Advance">Advance</SelectItem><SelectItem value="Loan">Loan</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="lx-label">Principal</Label><Input type="number" className="mt-1" data-testid="advance-principal" value={form.principal || ""} onChange={(e) => setForm({ ...form, principal: e.target.value })} /></div>
            <div><Label className="lx-label">Date Requested</Label><DateField className="mt-1" data-testid="advance-date" value={form.date_requested || ""} onChange={(e) => setForm({ ...form, date_requested: e.target.value })} /></div>
            <div className="col-span-2"><Label className="lx-label">Reason</Label><Input className="mt-1" data-testid="advance-reason" value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-advance" onClick={book}>Book</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remittance dialog */}
      <Dialog open={!!remit} onOpenChange={(v) => !v && setRemit(null)}>
        <DialogContent className="max-w-lg" data-testid="remit-dialog">
          <DialogHeader><DialogTitle className="font-display">Confirm Remittance — {remit?.employee_name}</DialogTitle>
            <DialogDescription>Disburse the principal and set the repayment schedule.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-500">Disbursing {remit?.type} of <b>{fmtNum(remit?.principal)}</b>. This posts Dr {remit?.type === "Loan" ? "Staff Loans (1400)" : "Staff Advances (1360)"} / Cr Bank and starts the repayment schedule.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="lx-label">Disbursement Date</Label><DateField className="mt-1" data-testid="remit-date" value={rf.disburse_date || ""} onChange={(e) => setRf({ ...rf, disburse_date: e.target.value })} /></div>
            <div><Label className="lx-label">Bank / Account</Label>
              <SearchableSelect className="mt-1" testid="remit-bank" placeholder="Select bank"
                value={rf.bank_account_id || ""} onValueChange={(v) => setRf({ ...rf, bank_account_id: v })}
                options={banks.map((b) => ({ value: b.id, label: `${b.bank_name} · ${b.currency}` }))} />
            </div>
            <div className="col-span-2"><Label className="lx-label">Reference No</Label><Input className="mt-1" data-testid="remit-ref" value={rf.reference_no || ""} onChange={(e) => setRf({ ...rf, reference_no: e.target.value })} /></div>
            <div><Label className="lx-label">Repayment Type</Label>
              <Select value={rf.schedule_type} onValueChange={(v) => setRf({ ...rf, schedule_type: v, custom: v === "amortized" ? evenSplit(remit?.principal, Number(rf.months) || 3) : [] })}>
                <SelectTrigger className="mt-1" data-testid="remit-schedule-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="one_off">One-off (single month)</SelectItem><SelectItem value="amortized">Amortized (multi-month)</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="lx-label">Start Month</Label><Input type="month" className="mt-1" data-testid="remit-start" value={rf.start_period || ""} onChange={(e) => setRf({ ...rf, start_period: e.target.value })} /></div>
            {rf.schedule_type === "amortized" && <>
              <div><Label className="lx-label">Months</Label><Input type="number" min={1} max={60} className="mt-1" data-testid="remit-months" value={rf.months || ""} onChange={(e) => { const n = Number(e.target.value) || 0; setRf({ ...rf, months: e.target.value, custom: evenSplit(remit?.principal, n) }); }} /></div>
              <div />
              <div className="col-span-2">
                <Label className="lx-label">Monthly repayment amounts — editable, must total the principal</Label>
                <div className="mt-1 max-h-52 space-y-1.5 overflow-y-auto pr-1" data-testid="remit-custom-schedule">
                  {(rf.custom || []).map((amt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-24 shrink-0 font-mono-num text-xs text-gray-500">{addMonths(rf.start_period, i)}</span>
                      <Input type="number" className="h-8" data-testid={`remit-custom-${i}`} value={amt} onChange={(e) => setRf((s) => ({ ...s, custom: s.custom.map((x, j) => (j === i ? e.target.value : x)) }))} />
                    </div>
                  ))}
                </div>
                {(() => {
                  const tot = (rf.custom || []).reduce((a, x) => a + Number(x || 0), 0);
                  const ok = Math.abs(tot - Number(remit?.principal || 0)) < 0.01;
                  return <div className={`mt-1 text-[11px] font-medium ${ok ? "text-emerald-600" : "text-red-500"}`} data-testid="remit-custom-total">Total {fmtNum(tot)} / {fmtNum(remit?.principal)} {ok ? "✓ balanced" : "— must equal principal"}</div>;
                })()}
              </div>
            </>}
          </div>
          <div className="rounded-sm bg-gray-50 px-3 py-2 text-xs text-gray-600" data-testid="remit-preview">
            {rf.schedule_type === "amortized" ? `${(rf.custom || []).length} monthly installments starting ${rf.start_period}` : `One-off repayment of ${fmtNum(remit?.principal)} in ${rf.start_period}`}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemit(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-remit" onClick={confirmRemit}><HandCoins size={15} className="mr-1" />Confirm & Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-[95vw] sm:max-w-[620px] overflow-y-auto">
          {detail && <>
            <SheetHeader><SheetTitle className="font-display flex items-center gap-2">{detail.employee_name} — {detail.type} <Badge status={detail.status} /></SheetTitle></SheetHeader>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-sm border border-gray-200 p-3"><div className="lx-label">Principal</div><div className="lx-num">{fmtNum(detail.principal)}</div></div>
              <div className="rounded-sm border border-gray-200 p-3"><div className="lx-label">Outstanding</div><div className="lx-num font-bold text-[#1B4383]">{fmtNum(detail.outstanding)}</div></div>
              <div className="rounded-sm border border-gray-200 p-3"><div className="lx-label">Repaid</div><div className="lx-num">{fmtNum(detail.repaid)}</div></div>
              <div className="rounded-sm border border-gray-200 p-3"><div className="lx-label">Installment</div><div className="lx-num">{fmtNum(detail.installment)} {detail.schedule_type === "amortized" ? `× ${detail.months}m` : "(one-off)"}</div></div>
            </div>
            {detail.reference_no && <div className="mt-2 text-xs text-gray-500">Disbursed {detail.disburse_date} via {detail.bank_name} · Ref {detail.reference_no}</div>}
            {detail.status === "Active" && <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50/50 p-3">
              <div className="lx-label mb-1 text-amber-700">Pause a month (hardship) — schedule auto-extends</div>
              <div className="flex items-center gap-2">
                <Input type="month" className="h-8 w-40 text-xs" data-testid="skip-month-input" value={skipMonth} onChange={(e) => setSkipMonth(e.target.value)} />
                <Button size="sm" variant="outline" className="border-amber-300 text-amber-700" data-testid="skip-month-btn" onClick={doSkip}>Skip this month</Button>
              </div>
              {(detail.skipped_periods || []).length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">
                {detail.skipped_periods.map((p) => (
                  <span key={p} data-testid={`skipped-chip-${p}`} className="inline-flex items-center gap-1 rounded-sm bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                    {p}<button className="hover:text-red-600" data-testid={`unskip-${p}`} onClick={() => doUnskip(p)}><XCircle size={13} /></button>
                  </span>
                ))}
              </div>}
            </div>}
            {(detail.schedule || []).length > 0 && <LoanScheduleEditor detail={detail} onSaved={(d) => { setDetail(d); load(); }} />}
            <div className="mt-4">
              <div className="lx-label mb-1 text-gray-500">Repayment History</div>
              {(detail.repayments || []).length === 0 ? <div className="text-xs text-gray-400">No repayments deducted yet.</div> :
                <table className="w-full text-xs"><thead className="bg-gray-50"><tr className="lx-label"><th className="px-2 py-1 text-left">Period</th><th className="px-2 py-1 text-right">Amount</th><th className="px-2 py-1 text-left">Date</th></tr></thead>
                  <tbody>{detail.repayments.map((r, i) => <tr key={i} className="border-b border-gray-100"><td className="px-2 py-1">{r.period}</td><td className="px-2 py-1 text-right font-mono-num">{fmtNum(r.amount)}</td><td className="px-2 py-1">{(r.date || "").slice(0, 10)}</td></tr>)}</tbody>
                </table>}
            </div>
          </>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
